import { Box, Button, Grid, Step, StepConnector, stepConnectorClasses, StepIconProps, StepLabel, Stepper, styled, TextField, Tooltip, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { CARD_NUMBER_REGEX, ER_RATE_INPUT, IBAN_REGEX, IRAN_MOBILE_REGEX } from '../../constants/regex.ts';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import { CampaignContext } from '../../contexts/CampaignContext.tsx';
import axios from 'axios';
import InstagramIcon from '../../assets/instagram.svg';
import EitaaIcon from '../../assets/itaa.svg';
import TelegramIcon from '../../assets/telegram.svg';
import Bale from '../../assets/bale-logo.png';
import Rubika from '../../assets/rubika.png';
import IResponse from '../../interfaces/response.ts';
import { useNavigate, useParams } from 'react-router-dom';
import { ICategory } from '../../interfaces/campaign.ts';
import PriceList from './PriceList.tsx';
import { Check, Info } from '@mui/icons-material';
import { InfoRounded } from '@mui/icons-material';
import { formatToKMB } from '../../utils/number.ts';

type IPages = {
	isCreate?: boolean;
};

const iconMap: Record<string, any> = {
	instagram: InstagramIcon,
	eita: EitaaIcon,
	telegram: TelegramIcon,
	bale: Bale,
	influenceral: InstagramIcon,
	rubika: Rubika,
};

type media_entities = {
	media_id: string;
	title: string;
	followers_count: string;
	category_id: string;
	er: string;
	likes_count: string;
	comments_count: string;
	handle_name: string;
	external_link: string;
	active_at: Date | string;
	image: string | null;
	company_id: number;
	rules: string[];
	extra: {
		card_number: string;
		phone: string;
		user_name: string;
		admin_name: string;
		admin_phone: string;
		description: string;
		external_link: string;
		telegram_bot_id: string | null;
		telegram_handle_name: string | null;
		instagram_handle_name: string | null;
	};
	updated_at: string;
	created_at: string;
	id: number;
	price: number | null;
};

const NewPage: FC<IPages> = ({ isCreate }) => {
	const [activeStep, setActiveStep] = useState(0);
	const [skipped, setSkipped] = useState(new Set<number>());
	const [mediaId, setMediaId] = useState<any>(null);
	const { t } = useTranslation();
	const { fetchedCategories, fetchedMedias, setFetchedCategories } = useContext(CampaignContext);
	const [selectedSocialMedia, setSelectedSocialMedia] = useState<number>();
	const [rules, setRules] = useState<string[]>(['']);
	const filteredIsSocial = fetchedMedias?.filter(media => media.is_social);
	const filterWebsite = fetchedMedias?.find(media => media.id === 10);
	const filteredMedias = filterWebsite && filteredIsSocial?.concat(filterWebsite);
	const { id } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		axios<IResponse<ICategory[]>>({
			url: 'options/category',
		}).then(res => {
			if (res.data.success) {
				setFetchedCategories(res.data.data);
			}
		});
	}, []);

	const pageSchema = z.object({
		media_id: z.number().min(1, t('category_required_message')),
		user_name: z.string().min(3, t('invalid_min_title_message')),
		phone: z.string().regex(IRAN_MOBILE_REGEX, { message: t('invalid_phone_message') }),
		admin_name: z.string().min(3, t('invalid_min_title_message')),
		telegram_handle_name: z.string(),
		admin_phone: z.string().regex(IRAN_MOBILE_REGEX, { message: t('invalid_phone_message') }),
		card_number: z.string().refine(val => CARD_NUMBER_REGEX.test(val), { message: t('invalid_card_message') }),
		sheba_number: z.string().refine(val => IBAN_REGEX.test(val.toUpperCase()), { message: t('invalid_iban_message') }),
		category_id: z.number().min(1, t('category_required_message')),
		followers_count: z.coerce.number().min(1, t('invalid_followers_count_message')),
		title: z.string().min(2, t('invalid_page_name_message')),
		likes_count: selectedSocialMedia == 2 ? z.coerce.number({ invalid_type_error: t('expected_number_received_nan') }).min(2, t('invalid_average_likes_message')) : z.coerce.number().optional().default(0),
		comments_count: selectedSocialMedia == 2 ? z.coerce.number({ invalid_type_error: t('expected_number_received_nan') }).min(2, t('invalid_average_comments_message')) : z.coerce.number().optional().default(0),
		views_story_count: selectedSocialMedia == 2 ? z.coerce.number({ invalid_type_error: t('expected_number_received_nan') }).min(2, t('invalid_average_story_message')) : z.coerce.number().optional().default(0),
		average_views_count: z.string(),
		handle_name: z.string().min(2, t('invalid_username_message')),
		external_link: z.string().min(2, t('string_should_not_be_empty')),
		er: z.coerce
			.string()
			.regex(ER_RATE_INPUT, { message: t('invalid_er_percentage_message') })
			.optional(),
		description: z.string().optional(),
	});

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		control,
		getValues,
		setValue,
		watch,
	} = useForm<z.infer<typeof pageSchema>>({
		resolver: zodResolver(pageSchema),
		defaultValues: {
			category_id: 0,
			media_id: 0,
			user_name: '',
			phone: '',
			admin_name: '',
		},
	});

	let member_value = getValues('average_views_count');
	let follower_count = getValues('followers_count');

	const handlePageGiver = (data: z.infer<typeof pageSchema>) => {
		axios<IResponse<media_entities>>({
			url: id ? `/media_entities/${id}` : '/media_entities',
			method: id ? 'PUT' : 'POST',
			loading: true,
			data: { ...data, rules: rules, average_views_count: parseInt(data.average_views_count) || null, sheba_number: data.sheba_number.replace('IR'.toLowerCase(), '') },
		}).then(response => {
			if (response?.data?.success) {
				setActiveStep(2);
				navigate(`/my-social-media/edit/${response.data.data.id}`);
			}
		});
	};

	const handleSubmitWithErrors = (data: z.infer<typeof pageSchema>) => {
		if (Object.keys(errors).length > 0) {
			return;
		}

		handlePageGiver(data);
	};

	const handleReset = () => {
		if (isCreate) {
			reset();
		} else {
			navigate('/my-social-media/list-of-my-pages/');
		}
	};

	useEffect(() => {
		if (!isCreate) {
			axios({
				url: `/media_entities/show/${id}`,
				method: 'GET',
			}).then(response => {
				if (response?.data?.success) {
					setActiveStep(2);
					setSelectedSocialMedia(response.data.data.media_id);
					setValue('media_id', response.data.data.media_id);
					setValue('user_name', response.data.data.user_name);
					setMediaId(response.data.data.media_id);
					setValue('phone', response.data.data.phone);
					setValue('admin_name', response.data.data.admin_name);
					setValue('telegram_handle_name', response.data.data.telegram_handle_name);
					setValue('admin_phone', response.data.data.admin_phone);
					setValue('card_number', response.data.data.card_number);
					setValue('sheba_number', 'IR' + response.data.data.sheba_number);
					setValue('category_id', response.data.data.category_id);
					setValue('followers_count', response.data.data.followers_count);
					setValue('title', response.data.data.title);
					setValue('likes_count', response.data.data.likes_count);
					setValue('comments_count', response.data.data.comments_count);
					setValue('handle_name', response.data.data.handle_name);
					setValue('views_story_count', response.data.data.views_story_count);
					setValue('average_views_count', String(response.data.data.average_views_count));
					setValue('external_link', response.data.data.external_link);
					setValue('er', response.data.data.er);
					setValue('description', response.data.data.description);
					setRules(response.data.data.rules);
				}
			});
		}
	}, []);

	const handleRulesChange = (e: ChangeEvent<HTMLInputElement>) => {
		const text = [e.target.value];
		setRules(text);
	};

	const isStepSkipped = (step: number) => {
		return skipped.has(step);
	};

	const handleNext = () => {
		let newSkipped = skipped;
		if (isStepSkipped(activeStep)) {
			newSkipped = new Set(newSkipped.values());
			newSkipped.delete(activeStep);
		}

		setActiveStep(prevActiveStep => prevActiveStep + 1);
		setSkipped(newSkipped);
	};

	const handleBack = () => {
		setActiveStep(prevActiveStep => prevActiveStep - 1);
	};

	const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
		[`&.${stepConnectorClasses.alternativeLabel}`]: {
			top: 22,
		},
		[`&.${stepConnectorClasses.active}`]: {
			[`& .${stepConnectorClasses.line}`]: {
				backgroundColor: '#1191D0',
			},
		},
		[`&.${stepConnectorClasses.completed}`]: {
			[`& .${stepConnectorClasses.line}`]: {
				backgroundColor: '#1191D0',
			},
		},
		[`& .${stepConnectorClasses.line}`]: {
			height: 3,
			border: 0,
			backgroundColor: '#eaeaf0',
			borderRadius: 1,
			...theme.applyStyles('dark', {
				backgroundColor: theme.palette.grey[800],
			}),
		},
	}));

	const ColorlibStepIconRoot = styled('div')<{
		ownerState: { completed?: boolean; active?: boolean };
	}>(({ theme }) => ({
		backgroundColor: '#ccc',
		zIndex: 1,
		color: '#fff',
		width: 30,
		height: 30,
		display: 'flex',
		borderRadius: '50%',
		justifyContent: 'center',
		alignItems: 'center',
		...theme.applyStyles('dark', {
			backgroundColor: theme.palette.grey[700],
		}),
		variants: [
			{
				props: ({ ownerState }) => ownerState.active,
				style: {
					backgroundColor: '#1191D0',
					boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
				},
			},
			{
				props: ({ ownerState }) => ownerState.completed,
				style: {
					backgroundColor: '#1191D0',
				},
			},
		],
	}));

	function ColorlibStepIcon(props: StepIconProps) {
		const { active, completed, className } = props;

		return (
			<ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
				{completed || active ? <Check className="QontoStepIcon-completedIcon" /> : <div className="QontoStepIcon-circle" />}
			</ColorlibStepIconRoot>
		);
	}

	return (
		<Box className="flex w-full flex-col items-center gap-y-8">
			<Box className="w-2/3">
				<Stepper activeStep={activeStep} connector={<ColorlibConnector />}>
					<Step key={'choosing_a_social_network'}>
						<StepLabel StepIconComponent={ColorlibStepIcon}>{t('choosing_a_social_network')}</StepLabel>
					</Step>
					<Step key={'entering_information'}>
						<StepLabel StepIconComponent={ColorlibStepIcon}>{t('entering_information')}</StepLabel>
					</Step>
					<Step key={'entering_a_tariff'}>
						<StepLabel StepIconComponent={ColorlibStepIcon}>{t('entering_a_tariff')}</StepLabel>
					</Step>
				</Stepper>
			</Box>
			<div className="flex w-full flex-col">
				<Box className="flex w-full justify-between">
					<Button onClick={handleBack} disabled={activeStep === 0 ? true : false}>
						{t('before')}
					</Button>
					<Button onClick={handleNext} disabled={activeStep === 2 ? true : false}>
						{t('after')}
					</Button>
				</Box>
				<Box className="my-4 flex rounded-md border border-dashed border-primary-300 bg-blue-50 px-6 py-4">
					<Info className="me-2 h-6 w-6 text-primary-600" />
					<Box className="text-sm leading-7 text-text-secondary">
						<ol className="ps-2 text-base text-text-primary">{t('create_tariff_guide')}</ol>
					</Box>
				</Box>
				{activeStep === 0 && (
					<div className="my-5 flex w-full flex-col rounded-lg bg-white">
						<div className="flex flex-col items-center py-10">
							<Typography className="mb-3 text-sm font-medium">{t('social_media')}</Typography>
							<Controller
								control={control}
								name="media_id"
								render={({ field }) => (
									<Autocomplete
										disablePortal
										options={filteredMedias || []}
										getOptionLabel={option => option.title}
										filterSelectedOptions
										disabled={!isCreate}
										value={filteredMedias?.find(option => option.id === field.value) || null}
										isOptionEqualToValue={(option, value) => option.id === value.id}
										sx={{ width: 500 }}
										onChange={(_, value) => {
											field.onChange(value?.id || null);
											setSelectedSocialMedia(value?.id);
											setActiveStep(activeStep + 1);
										}}
										renderInput={params => <TextField {...params} placeholder={t('choose')} label={t('social_media')} error={!!errors.media_id} helperText={errors.media_id?.message} />}
										renderOption={(props, option) => {
											return (
												<li {...props} key={option.id}>
													<img src={iconMap[option.system_name]} className={'me-2 h-5 w-5'} />
													{option.title}
												</li>
											);
										}}
									/>
								)}
							/>
						</div>
					</div>
				)}
				{activeStep === 1 && (
					<div className="p-6">
						{(selectedSocialMedia && selectedSocialMedia > 0) || id ? (
							<>
								<Typography>{t('to_register_as_an_advertiser_please_enter_your_information')}</Typography>
								<form className="flex w-full flex-col gap-6 rounded bg-background-paper px-8 pb-0.5 pt-6 text-base" onSubmit={handleSubmit(handleSubmitWithErrors)}>
									<div className="mb-4 border-s-2 border-primary-main p-4">
										<Typography className="mb-2">{t('user_info')}</Typography>
										<Grid container spacing={2}>
											<Grid item xs={6}>
												<TextField InputLabelProps={{ shrink: !!watch('user_name') }} fullWidth size="small" label={t('user_name_and_family')} {...register('user_name')} error={!!errors.user_name} helperText={errors.user_name?.message} />
											</Grid>
											<Grid item xs={6}>
												<TextField InputLabelProps={{ shrink: !!watch('phone') }} fullWidth size="small" label={t('contact_number')} {...register('phone')} error={!!errors.phone} helperText={errors.phone?.message} />
											</Grid>
										</Grid>
									</div>

									{mediaId !== 10 && (
										<div className="mb-4 border-s-2 border-primary-main p-4">
											<Typography className="mb-2">{t('admin_info')}</Typography>
											<Grid container spacing={2}>
												<Grid item xs={6}>
													<TextField fullWidth size="small" label={t('admin_name')} InputLabelProps={{ shrink: !!watch('admin_name') }} {...register('admin_name')} error={!!errors.admin_name} helperText={errors.admin_name?.message} />
												</Grid>
												<Grid item xs={6} className="relative">
													<Tooltip title={t('info_admin_id')} className="absolute left-2 z-50">
														<Box className="my-2 flex items-center">
															<InfoRounded color="primary" fontSize="small" />
															<Typography className="text-text-secondary">{t('info')}</Typography>
														</Box>
													</Tooltip>
													<TextField fullWidth size="small" label={t('admin_id_for_connection')} InputLabelProps={{ shrink: !!watch('telegram_handle_name') }} {...register('telegram_handle_name')} error={!!errors.telegram_handle_name} helperText={errors.telegram_handle_name?.message} />
												</Grid>
												<Grid item xs={6}>
													<TextField fullWidth size="small" label={t('contact_number')} InputLabelProps={{ shrink: !!watch('admin_phone') }} {...register('admin_phone')} error={!!errors.admin_phone} helperText={errors.admin_phone?.message} />
												</Grid>
												<Grid item xs={6}>
													<TextField fullWidth size="small" label={t('number_card')} InputLabelProps={{ shrink: !!watch('card_number') }} {...register('card_number')} error={!!errors.card_number} helperText={errors.card_number?.message} />
												</Grid>
												<Grid item xs={6}>
													<TextField fullWidth size="small" label={t('number_IBAN')} InputLabelProps={{ shrink: !!watch('sheba_number') }} {...register('sheba_number')} error={!!errors.sheba_number} helperText={errors.sheba_number?.message} />
												</Grid>
											</Grid>
										</div>
									)}

									<div className="border-s-2 border-primary-main p-4">
										<Typography className="mb-2">{t('page_info')}</Typography>
										<Grid container spacing={2}>
											<Grid item xs={4}>
												<Controller
													control={control}
													name="category_id"
													render={({ field }) => (
														<Autocomplete
															size="small"
															disablePortal
															options={fetchedCategories || []}
															getOptionLabel={option => option.title}
															filterSelectedOptions
															isOptionEqualToValue={(option, value) => option.id === value.id}
															onChange={(_, selectedOption) => {
																field.onChange(selectedOption?.id || 0);
															}}
															value={fetchedCategories?.find(option => option.id === field.value) || ({ title: '' } as ICategory)}
															renderInput={params => <TextField {...params} label={t('category')} InputLabelProps={{ shrink: !!watch('category_id') }} error={!!errors.category_id} helperText={errors.category_id?.message} />}
														/>
													)}
												/>
											</Grid>
											<Grid item xs={4}>
												<TextField fullWidth size="small" type="number" label={t(`follower_count`) + ' ' + ` (${formatToKMB(Number(follower_count ?? 0) ?? 0)})`} {...register('followers_count')} InputLabelProps={{ shrink: !!watch('followers_count') }} error={!!errors.followers_count} helperText={errors.followers_count?.message} />
											</Grid>
											<Grid item xs={4}>
												<TextField fullWidth size="small" label={t('persian_page_name')} {...register('title')} error={!!errors.title} InputLabelProps={{ shrink: !!watch('title') }} helperText={errors.title?.message} />
											</Grid>
										</Grid>

										{selectedSocialMedia === 2 && (
											<Grid container spacing={2} style={{ marginTop: 0 }}>
												<Grid item xs={4}>
													<TextField fullWidth size="small" label={t('average_likes')} {...register('likes_count')} error={!!errors.likes_count} InputLabelProps={{ shrink: !!watch('likes_count') }} helperText={errors.likes_count?.message} />
												</Grid>
												<Grid item xs={4}>
													<TextField fullWidth size="small" label={t('average_comments')} {...register('comments_count')} error={!!errors.comments_count} InputLabelProps={{ shrink: !!watch('comments_count') }} helperText={errors.comments_count?.message} />
												</Grid>
												<Grid item xs={4}>
													<TextField fullWidth size="small" label={t('story_views')} {...register('views_story_count')} error={!!errors.views_story_count} InputLabelProps={{ shrink: !!watch('views_story_count') }} helperText={errors.views_story_count?.message} />
												</Grid>
											</Grid>
										)}

										<Grid container spacing={2} style={{ marginTop: 0 }}>
											<Grid item xs={6} className="relative">
												<TextField fullWidth size="small" label={t(`24_hour_visit`) + ' ' + ` (${formatToKMB(Number(member_value ?? 0) ?? 0)})`} {...register('average_views_count')} error={!!errors.average_views_count} helperText={errors.average_views_count?.message} InputLabelProps={{ shrink: !!watch('average_views_count') }} />
											</Grid>
											<Grid item xs={6} className="relative">
												<TextField fullWidth size="small" label={t('telegram_instagram_id')} {...register('handle_name')} error={!!errors.handle_name} helperText={errors.handle_name?.message} InputLabelProps={{ shrink: !!watch('handle_name') }} />
											</Grid>
											<Grid item xs={6}>
												<TextField fullWidth size="small" label={t('chanel-tag')} {...register('external_link')} error={!!errors.external_link} helperText={errors.external_link?.message} InputLabelProps={{ shrink: !!watch('external_link') }} />
											</Grid>
											<Grid item xs={6}>
												<TextField fullWidth size="small" label={t('er')} {...register('er')} error={!!errors.er} helperText={errors.er?.message} InputLabelProps={{ shrink: !!watch('er') }} />
											</Grid>
										</Grid>

										<Grid container spacing={2} style={{ marginTop: 0 }}></Grid>
										<Grid item xs={12} className="mt-4">
											<Tooltip title={t('info_from_influencer_to_admin_user')}>
												<Box className="my-2 flex w-1/6 items-center">
													<InfoRounded color="primary" fontSize="small" />
													<Typography>{t('description_info')}</Typography>
												</Box>
											</Tooltip>
											<TextField fullWidth size="small" multiline rows={4} label={t('description')} {...register('description')} error={!!errors.description} InputLabelProps={{ shrink: !!watch('description') }} helperText={errors.description?.message} />
										</Grid>
										<Grid item xs={12} className="mt-4">
											<Tooltip title={t('about_influencer')}>
												<Box className="my-2 flex w-1/6 items-center">
													<InfoRounded color="primary" fontSize="small" />
													<Typography>{t('rules_info')}</Typography>
												</Box>
											</Tooltip>
											<TextField fullWidth size="small" multiline rows={4} label={t('rules')} InputLabelProps={{ shrink: true }} defaultValue={rules} onChange={handleRulesChange} />
										</Grid>
									</div>
									<div className="mb-8 me-4 mt-5 flex flex-row-reverse gap-3">
										<Button variant="contained" className="my-2 w-28" type="submit">
											{t('save')}
										</Button>
										<Button variant="outlined" className="my-2 w-20 border-grey-400 text-grey-400" onClick={handleReset}>
											{t('cancel')}
										</Button>
									</div>
								</form>
							</>
						) : selectedSocialMedia ? (
							<div className="flex h-64 items-center justify-center">
								<Typography variant="h6" color="textSecondary">
									{t('plz_wait_for_this_feature')}
								</Typography>
							</div>
						) : null}
					</div>
				)}
				{activeStep === 2 && !isCreate ? (
					<>
						<PriceList id={parseInt(id ?? '0')} selectedSocialMedia={selectedSocialMedia ?? 1} />
					</>
				) : (
					<></>
				)}
			</div>
		</Box>
	);
};

export default NewPage;
